import React   from 'react';
import styled from 'styled-components';
import {
    BrowserRouter as Router,
    Routes,
    Route,
    Navigate,
}                 from 'react-router-dom';
import Account    from './component/menu/account';
import Menu       from './menu';
import pages      from "./pages";
import storage    from "./utils/storage";
import { ws }     from "./utils";
import NotFound   from "./page/error/not_found";
import * as utils from './utils';
import preval     from 'preval.macro';
import Features   from "./component/menu/feature";

const StyledExtraInfo = styled.div`
    position: fixed;
    right: 1rem;
    bottom: 1rem;
    display: flex;
    flex-direction: column;
    opacity: .3;
`;

const StyledRelease = styled.div`
    font-size: .6rem;
`;

const StyledClock = styled.div`
    display: flex;
    flex-direction: column;
    align-items: end;
`;

/**
 * The clock in the corner, in its own component (agers-webapp-6): it used to live in Body's
 * state with a 100 ms setState, which re-rendered the whole app (router, menu, the page) ten
 * times a second. Now only this text re-renders, once a second (an unchanged string is a no-op).
 */
const Clock = () => {
    const [clock, setClock] = React.useState(() => utils.formattedDate(new Date(), `Y-m-d H:i:s`));

    React.useEffect(() => {
        const timer = setInterval(() => setClock(utils.formattedDate(new Date(), `Y-m-d H:i:s`)), 250);
        return () => clearInterval(timer);
    }, []);

    return <StyledClock>{clock}</StyledClock>;
};

interface Props {}
interface State {
    login: boolean;
    functions: { [name: string]: (...parameter: any) => void };
}

// "Last release" is the commit time when the build sets SOURCE_DATE_EPOCH (seconds; agers-home's
// web-box deploy-site.sh exports it), so two builds of one commit are identical. Otherwise it is the
// build clock, as before (agers-webapp-8).
const lastReleaseDate: Date = preval`module.exports = process.env.SOURCE_DATE_EPOCH ? new Date(Number(process.env.SOURCE_DATE_EPOCH) * 1000) : new Date();`;
class Body extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            login: false,
            functions: {
                setLogin: (state: boolean): void => this.state.login !== state
                    ? this.setState(
                        {login: state},
                        () => storage.account.set.key(`login`, state ? `true` : `false`)
                    )
                    : undefined,
            }
        };
    }

    componentDidMount() {
        // ws.connect();

        ws.listener.message(
            [`account.verify`],
            data => {
                if (
                    data.code === 200
                     && typeof data.results === `object`
                    && data.results.verified
                ) {
                    this.setState({login : true});
                } else {
                    storage.account.remove.keys(
                        `username`,
                        `avatar`,
                        `token`,
                        `validTo`,
                    );
                }
            }
        );

        ws.listener.message(
            [`account.logout`],
            data => data.code === 200 && this.setState({login : false}, () => storage.account.remove.keys(`username`, `avatar`, `token`, `validTo`))
        );

        const token = storage.account.get(`token`);
        if (typeof token === `string`) {
            ws.sent(
                `account`,
                `verify`,
                {
                    token: token
                },
            );
        }
    }

    render() {
        return <Router>
            <Menu
                pages={pages}
                login={this.state.login}
            />
            <Account
                {...this.state}
            />
            <Features.Feedback />
            <Routes>
                {
                    [true, false].includes(this.state.login)
                    && pages.map(
                        pageInfo => pageInfo.link.map(
                            (link, index: number) => {
                                if (
                                    pageInfo.hasOwnProperty(`props`)
                                    && pageInfo.props !== undefined
                                ) {
                                    switch (true) {
                                        case pageInfo.props.enabled === false:
                                            return undefined;
                                        case pageInfo.props.login !== undefined && pageInfo.props.login !== this.state.login:
                                            return pageInfo.props.redirect !== undefined
                                                ? <Route
                                                    key={index}
                                                    path={link}
                                                    element={<Navigate replace to={pageInfo.props.redirect} />}
                                                />
                                                : undefined;
                                        default:
                                            break;
                                    }
                                }

                                return <Route
                                    key={index}
                                    path={link}
                                    element={pageInfo.page}
                                />
                            }
                        )
                    )
                }
                {/* The News page is retired (agers-websocket-17): its old links go to the home page. */}
                <Route path={`/news/*`} element={<Navigate replace to={`/`} />}/>
                <Route path={`*`} element={<NotFound/>}/>
            </Routes>
            <StyledExtraInfo>
                <Clock/>
                {
                    utils.isDevelopment()
                    && <StyledRelease>Last release: {utils.formattedDate(new Date(lastReleaseDate))}</StyledRelease>
                }
            </StyledExtraInfo>
        </Router>;
    }
}

export default Body;